"use client";

import { useState } from "react";

import { Button } from "@/components/ui/button";
import { useUpdateOnboardingStateMutation } from "@/hooks/useOnboarding";
import { logoutAndRedirect } from "@/lib/session/client";

/**
 * The usage-tracking terms copy, with no controls. Single source of truth for
 * the consent wording — rendered by UsageConsentCard on both consent surfaces
 * and read-only on the Privacy and data settings page. Keep in sync with
 * UsageConsent.CurrentVersion: a material change here requires a version bump.
 */
export function UsageConsentTerms() {
  return (
    <>
      <p className="text-sm text-muted-foreground">
        Scirix records how you use the product. This helps us improve Scirix and the Reality
        Model, and it keeps an accurate record of use that protects both you and us. We record
        and process:
      </p>
      <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm text-muted-foreground">
        <li>
          <span className="font-medium text-foreground">Prompts and queries</span> you submit to
          Scirix agents and the Reality Model.
        </li>
        <li>
          <span className="font-medium text-foreground">Product usage</span>, such as the features
          you use and the actions you take.
        </li>
        <li>
          <span className="font-medium text-foreground">Technical data points</span>, such as
          timestamps, namespace and run identifiers, and error diagnostics.
        </li>
        <li>
          <span className="font-medium text-foreground">Repository content</span> in the
          repositories you link. We never read this data ourselves; it is processed automatically
          by our analyzer and, through it, by the configured LLM providers.
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Some of this data is processed by <span className="font-medium text-foreground">third-party
        services</span> during analysis. For example, repository and documentation content that goes
        into the Reality Model is embedded by a third-party provider (OpenAI by default, unless your
        deployment is configured otherwise), and prompts are handled by the configured LLM
        providers.
      </p>
      <p className="text-sm text-muted-foreground">
        We <span className="font-medium text-foreground">never sell your data</span>. You can ask us
        to delete your data at any time, and we may also delete it for operational reasons.
      </p>
    </>
  );
}

/**
 * The usage-tracking consent terms + accept/decline controls. Rendered inline
 * by the onboarding flow and as a blocking overlay by UsageConsentGate for
 * users who are already onboarded. Acceptance is recorded via
 * PATCH /api/me/onboarding { acceptUsageConsent }; the server stamps the
 * consent version and timestamp. Declining signs the user out.
 */
export function UsageConsentCard() {
  const updateOnboarding = useUpdateOnboardingStateMutation();
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  async function handleAccept() {
    setError(null);
    try {
      await updateOnboarding.mutateAsync({ acceptUsageConsent: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not record your consent. Please try again.",
      );
    }
  }

  async function handleDecline() {
    setSigningOut(true);
    await logoutAndRedirect();
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-base font-semibold">Usage tracking and your data</h2>
      <UsageConsentTerms />
      <p className="text-sm text-muted-foreground">
        You need to accept this to use Scirix.
      </p>
      <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border/60 bg-muted/30 px-3 py-2.5 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 shrink-0"
          checked={checked}
          onChange={(event) => setChecked(event.target.checked)}
        />
        <span>
          I understand and agree that Scirix records my usage, prompts, and the data points listed
          above, and that some data is processed by third-party services.
        </span>
      </label>
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={handleDecline} disabled={signingOut}>
          {signingOut ? "Signing out…" : "Decline and sign out"}
        </Button>
        <Button
          onClick={handleAccept}
          disabled={!checked || updateOnboarding.isPending || signingOut}
        >
          {updateOnboarding.isPending ? "Saving…" : "Agree and continue"}
        </Button>
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
